import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EvtHeader } from "components/header";
import { CustomList, Icon } from "components";
import AvatarDisplay from "components/avatar/display";
import { Badge } from "ui/components/badge";
import { Button } from "ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/components/dialog";
import { eventBus } from "utils/eventBus";
import { RelativeDateCell } from "@/components/date/cell";
import { repoQuery } from "../layout";
import { resetBranch } from "../api";

const ENDPOINT = "github/repository/branch_commits";

const EVENT_LABELS = {
  push: "Push",
  force_push: "Force Push",
  branch_creation: "Branch Created",
  pr_merge: "PR Merge"
};

// /integrations/github/repositories/:repo_id/branches/:branch_name
// Branch activity (no pagination) with "reset HEAD to this commit".
export default function GithubBranchCommits() {
  const { repo_id, branch_name } = useParams();
  const branch = decodeURIComponent(branch_name || "");
  const queryClient = useQueryClient();
  // Only job coordinators can reset a branch.
  const canReset = !!useSelector(selectUser)?.role?.is_job_coordinator;
  const [head, setHead] = useState(null);
  const [target, setTarget] = useState(null); // row selected for reset

  const { data: repo } = useQuery(repoQuery(repo_id));
  const repository_url = repo?.repository_url;
  const storageKey = `integrations-github-branch-commits-${repo_id}-${branch}`;

  const reset = useMutation({
    mutationFn: () =>
      resetBranch({
        repository_url,
        branch,
        current_head: head,
        expected_head: target?.id,
      }),
    onSuccess: async () => {
      toast.success(`${branch} reset to ${target?.id?.slice(0, 7)}`);
      setTarget(null);
      // List fetches go through queryClient.fetchQuery, so drop the cache before refreshing.
      await queryClient.invalidateQueries({ queryKey: [ENDPOINT] });
      queryClient.invalidateQueries({ queryKey: ["github/repository/branches"] });
      queryClient.invalidateQueries({ queryKey: ["integrations-github-branch-summary", repository_url] });
      eventBus.emit("refresh:list", `${ENDPOINT}${storageKey}`);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to reset branch");
    },
  });

  const columns = useMemo(
    () => [
      {
        id: "type",
        header: "Event",
        accessorKey: "type",
        cell: ({ getValue }) => (
          <Badge variant={getValue() === "force_push" ? "destructive" : "outline"}>
            {EVENT_LABELS[getValue()] || getValue()}
          </Badge>
        ),
      },
      {
        id: "message",
        header: "Message",
        accessorKey: "message",
        size: 420,
        cell: ({ row }) => (
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate" title={row.original.message}>
              {row.original.message || "-"}
            </span>
            {row.original.is_current_head && <Badge>HEAD</Badge>}
          </div>
        ),
      },
      {
        id: "sha",
        header: "Commit",
        accessorKey: "id",
        cell: ({ row }) => (
          <a
            href={row.original.commit_url}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-xs hover:underline"
            title={row.original.id}
          >
            {row.original.id?.slice(0, 6)+ "..." + row.original.id?.slice(-6)}
          </a>
        ),
      },
      {
        id: "actor",
        header: "Author",
        accessorKey: "actor",
        cell: ({ getValue }) => {
          const actor = getValue();
          return (
            <div className="flex items-center gap-2">
              {actor?.id && <AvatarDisplay className="w-7 h-7" user={actor} />}
              <span>{actor?.github_username || "-"}</span>
            </div>
          );
        },
      },
      {
        id: "date",
        header: "Date",
        accessorKey: "date",
        cell: RelativeDateCell,
      },
      canReset && {
        id: "actions",
        header: "",
        cell: ({ row }) =>
          !row.original.is_current_head && (
            <Button size="sm" variant="outline" onClick={() => setTarget(row.original)}>
              <Icon icon="lucide:rotate-ccw" className="size-4" />
              Reset here
            </Button>
          ),
      },
    ].filter(Boolean),
    [canReset]
  );

  return (
    <div className="flex-1">
      {/* <div className="py-2 flex items-center gap-1">
        <EvtHeader title={branch} />
        {repository_url && (
          <Button asChild size="icon" variant="ghost" className="size-7" title="Open branch on GitHub">
            <a href={`${repository_url}/tree/${branch}`} target="_blank" rel="noreferrer">
              <Icon icon="lucide:external-link" className="size-4" />
            </a>
          </Button>
        )}
      </div> */}

      {repository_url && (
        
          <CustomList
            title="Activity"
            storageKey={storageKey}
            endPoint={ENDPOINT}
            filters={{ repository_url, branch }}
            columns={columns}
            perPage={100}
            sortBy=""
            showSearch={false}
            hidePagination
            // All events of the current HEAD carry is_current_head; use that sha as current_head.
            onDataChange={(res) =>
              setHead(res?.items?.find((item) => item.is_current_head)?.id ?? null)
            }
          />
      )}

      <Dialog open={!!target} onOpenChange={(open) => !open && !reset.isPending && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset branch?</DialogTitle>
            <DialogDescription>
              Reset <span className="font-medium">{branch}</span> from{" "}
              <span className="font-mono">{head?.slice(0, 7)}</span> to{" "}
              <span className="font-mono">{target?.id?.slice(0, 7)}</span>. This rewrites the
              branch history on GitHub.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={reset.isPending} onClick={() => setTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={reset.isPending || !head}
              onClick={() => reset.mutate()}
            >
              {reset.isPending ? "Resetting..." : "Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
